import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import { SidebarProvider, useSidebar } from '../../hooks/useSidebar';
import MetricCard from './components/MetricCard';
import ChartCard from './components/ChartCard';
import PermitVolumeChart from './components/PermitVolumeChart';
import HazardDistributionChart from './components/HazardDistributionChart';
import LocationComparisonChart from './components/LocationComparisonChart';
import ComplianceChart from './components/ComplianceChart';
import FilterPanel from './components/FilterPanel';
import HazardAnalyticsTable from './components/HazardAnalyticsTable';
import SafetyEquipmentChart from './components/SafetyEquipmentChart';
import BehavioralSafetyMetrics from './components/BehavioralSafetyMetrics';
import BehavioralSafetyTrendChart from './components/BehavioralSafetyTrendChart';
import IncidentManagementAnalytics from './components/IncidentManagementAnalytics';
import SafetyAuditPerformance from './components/SafetyAuditPerformance';
import KeyRisksStatus from './components/KeyRisksStatus';
import AIRecommendations from './components/AIRecommendations';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const SafetyAnalyticsDashboardContent = () => {
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  const [dateRange, setDateRange] = useState('last30days');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [permitType, setPermitType] = useState('all');

  const metricsData = [
    {
      title: "Active Permits",
      value: "247",
      change: "+12.5%",
      changeType: "positive",
      icon: "FileText",
      iconColor: "var(--color-primary)",
      trend: true
    },
    {
      title: "Pending Approvals",
      value: "18",
      change: "-8.3%",
      changeType: "positive",
      icon: "Clock",
      iconColor: "var(--color-warning)",
      trend: true
    },
    {
      title: "Overdue Permits",
      value: "5",
      change: "+2",
      changeType: "negative",
      icon: "AlertTriangle",
      iconColor: "var(--color-error)",
      trend: true
    },
    {
      title: "Safety Incidents",
      value: "3",
      change: "-40%",
      changeType: "positive",
      icon: "Shield",
      iconColor: "var(--color-success)",
      trend: true
    }
  ];

  // New behavioral safety metrics data
  const behavioralSafetyData = [
    {
      type: 'Fatalities',
      count: 0,
      trend: true,
      trendDirection: 'down',
      trendValue: '0%',
      period: 'Last 365 days'
    },
    {
      type: 'Lost Time Injury',
      count: 2,
      trend: true,
      trendDirection: 'down',
      trendValue: '33%',
      period: 'Last 30 days'
    },
    {
      type: 'Major Injury',
      count: 5,
      trend: true,
      trendDirection: 'down',
      trendValue: '17%',
      period: 'Last 30 days'
    },
    {
      type: 'Minor Injury',
      count: 12,
      trend: true,
      trendDirection: 'down',
      trendValue: '25%',
      period: 'Last 30 days'
    },
    {
      type: 'Near Miss',
      count: 34,
      trend: true,
      trendDirection: 'up',
      trendValue: '8%',
      period: 'Last 30 days'
    },
    {
      type: 'Unsafe Acts',
      count: 56,
      trend: true,
      trendDirection: 'down',
      trendValue: '12%',
      period: 'Last 30 days'
    },
    {
      type: 'Unsafe Conditions',
      count: 42,
      trend: true,
      trendDirection: 'down',
      trendValue: '15%',
      period: 'Last 30 days'
    }
  ];

  const behavioralSafetyTrendData = [
    { 
      month: 'Jul', 
      fatalities: 0, 
      lostTimeInjury: 3, 
      majorInjury: 6, 
      minorInjury: 16, 
      nearMiss: 28, 
      unsafeActs: 64, 
      unsafeConditions: 49 
    },
    { 
      month: 'Aug', 
      fatalities: 0, 
      lostTimeInjury: 2, 
      majorInjury: 7, 
      minorInjury: 14, 
      nearMiss: 31, 
      unsafeActs: 58, 
      unsafeConditions: 45 
    },
    { 
      month: 'Sep', 
      fatalities: 0, 
      lostTimeInjury: 4, 
      majorInjury: 5, 
      minorInjury: 15, 
      nearMiss: 29, 
      unsafeActs: 62, 
      unsafeConditions: 51 
    },
    { 
      month: 'Oct', 
      fatalities: 0, 
      lostTimeInjury: 3, 
      majorInjury: 6, 
      minorInjury: 13, 
      nearMiss: 32, 
      unsafeActs: 59, 
      unsafeConditions: 48 
    },
    { 
      month: 'Nov', 
      fatalities: 0, 
      lostTimeInjury: 2, 
      majorInjury: 4, 
      minorInjury: 11, 
      nearMiss: 35, 
      unsafeActs: 54, 
      unsafeConditions: 44 
    },
    { 
      month: 'Dec', 
      fatalities: 0, 
      lostTimeInjury: 2, 
      majorInjury: 5, 
      minorInjury: 12, 
      nearMiss: 34, 
      unsafeActs: 56, 
      unsafeConditions: 42 
    }
  ];

  const incidentManagementData = [
    { category: 'Chemical', reported: 12, investigating: 3, closed: 9 },
    { category: 'Electrical', reported: 8, investigating: 2, closed: 6 },
    { category: 'Fall/Slip', reported: 15, investigating: 4, closed: 11 },
    { category: 'Fire', reported: 5, investigating: 1, closed: 4 },
    { category: 'Equipment', reported: 10, investigating: 3, closed: 7 }
  ];

  const incidentSummaryMetrics = [
    {
      label: 'Total Incidents',
      value: '50',
      icon: 'AlertCircle',
      color: 'var(--color-primary)',
      subtext: 'Last 30 days'
    },
    {
      label: 'Under Investigation',
      value: '13',
      icon: 'Search',
      color: 'var(--color-warning)',
      subtext: '26% of total'
    },
    {
      label: 'Closed',
      value: '37',
      icon: 'CheckCircle',
      color: 'var(--color-success)',
      subtext: '74% resolution rate'
    },
    {
      label: 'Avg. Resolution Time',
      value: '4.5 days',
      icon: 'Clock',
      color: 'var(--color-accent)',
      subtext: '-12% from last month'
    }
  ];

  const auditPerformanceData = [
    { name: 'Excellent', value: 45 },
    { name: 'Good', value: 78 },
    { name: 'Satisfactory', value: 32 },
    { name: 'Needs Improvement', value: 12 }
  ];

  const auditScoreData = [
    {
      label: 'Overall Score',
      value: '87%',
      icon: 'Award',
      color: 'var(--color-success)',
      progress: 87,
      trend: 'up'
    },
    {
      label: 'Completed Audits',
      value: '167',
      icon: 'CheckSquare',
      color: 'var(--color-primary)',
      progress: 92,
      trend: 'up'
    },
    {
      label: 'Pending Actions',
      value: '23',
      icon: 'Clock',
      color: 'var(--color-warning)',
      progress: 65,
      trend: 'down'
    }
  ];

  const keyRisksData = [
    {
      title: 'Confined Space Entry Procedures',
      description: 'Multiple near-miss incidents reported in confined space operations across Plant 1 and Plant 3',
      level: 'Critical',
      status: 'In Progress',
      location: 'Plant 1, Plant 3',
      identifiedDate: 'Nov 28, 2025',
      owner: 'John Anderson',
      daysOpen: 8,
      icon: 'Box',
      mitigationActions: [
        { description: 'Review and update confined space entry procedures', completed: true },
        { description: 'Conduct mandatory training for all personnel', completed: true },
        { description: 'Install additional gas detection equipment', completed: false },
        { description: 'Schedule monthly safety drills', completed: false }
      ]
    },
    {
      title: 'Chemical Handling Compliance',
      description: 'Increasing trend in chemical exposure incidents requiring enhanced safety protocols',
      level: 'High',
      status: 'Open',
      location: 'Plant 2',
      identifiedDate: 'Dec 1, 2025',
      owner: 'Sarah Mitchell',
      daysOpen: 5,
      icon: 'Droplet',
      mitigationActions: [
        { description: 'Audit current chemical storage facilities', completed: false },
        { description: 'Implement new PPE requirements', completed: false },
        { description: 'Schedule chemical safety refresher training', completed: false }
      ]
    },
    {
      title: 'Fall Protection Equipment',
      description: 'Equipment inspection records show overdue maintenance on fall protection gear',
      level: 'High',
      status: 'Under Review',
      location: 'All Sites',
      identifiedDate: 'Nov 25, 2025',
      owner: 'Mike Rodriguez',
      daysOpen: 11,
      icon: 'ArrowDown',
      mitigationActions: [
        { description: 'Complete inspection of all fall protection equipment', completed: true },
        { description: 'Replace damaged or expired equipment', completed: false },
        { description: 'Update equipment tracking system', completed: false }
      ]
    },
    {
      title: 'Emergency Response Readiness',
      description: 'Recent audit identified gaps in emergency evacuation procedures and drills',
      level: 'Medium',
      status: 'In Progress',
      location: 'Warehouse 1',
      identifiedDate: 'Nov 30, 2025',
      owner: 'Lisa Chen',
      daysOpen: 6,
      icon: 'Siren',
      mitigationActions: [
        { description: 'Update emergency evacuation plans', completed: true },
        { description: 'Conduct emergency drill with all shifts', completed: false },
        { description: 'Install additional emergency lighting', completed: false }
      ]
    }
  ];

  const aiRecommendationsData = [
    {
      title: 'Enhance Confined Space Safety Training',
      summary: 'Analysis shows 67% increase in confined space near-miss incidents. Immediate training intervention recommended.',
      category: 'Behavioral Safety',
      priority: 'High',
      expectedImpact: '45% reduction in incidents',
      details: 'Historical data indicates that sites with quarterly confined space training show 45% fewer incidents. Current training frequency at affected locations is semi-annual.',
      actionSteps: [
        'Schedule immediate refresher training for all confined space authorized personnel',
        'Implement quarterly competency assessments',
        'Deploy real-time gas monitoring systems in high-risk areas',
        'Create mentorship program pairing experienced workers with new entrants'
      ],
      basedOn: ['Near Miss Trends', 'Training Records', 'Incident Data', 'Industry Benchmarks']
    },
    {
      title: 'Optimize Work Permit Approval Process',
      summary: 'Current average approval time of 4.2 hours can be reduced by 30% through workflow automation.',
      category: 'Permit Management',
      priority: 'Medium',
      expectedImpact: '30% faster approvals',
      details: 'Analysis of 247 active permits shows approval bottlenecks during supervisor handover periods. Implementing digital workflow with mobile approval capability can streamline the process.',
      actionSteps: [
        'Enable mobile permit approval for supervisors',
        'Implement automated routing based on permit type and location',
        'Set up real-time notifications for pending approvals',
        'Create escalation protocols for permits exceeding 3-hour approval time'
      ],
      basedOn: ['Permit Volume Data', 'Approval Time Metrics', 'Workflow Analysis']
    },
    {
      title: 'Proactive Chemical Safety Program',
      summary: 'Chemical exposure remains top hazard category. Predictive monitoring can prevent 60% of incidents.',
      category: 'Incident Prevention',
      priority: 'High',
      expectedImpact: '60% reduction in chemical incidents',
      details: 'Machine learning analysis of 145 chemical hazard instances reveals common precursor patterns including improper PPE selection, inadequate ventilation, and storage protocol violations.',
      actionSteps: [
        'Deploy AI-powered chemical hazard detection system',
        'Implement mandatory pre-work chemical risk assessments',
        'Upgrade ventilation systems in high-exposure areas',
        'Create chemical-specific safety protocols with visual guides'
      ],
      basedOn: ['Hazard Distribution', 'Incident Reports', 'PPE Usage Data', 'Environmental Monitoring']
    },
    {
      title: 'Strengthen Safety Audit Follow-through',
      summary: '23 pending audit actions represent compliance risk. Prioritized action plan recommended.',
      category: 'Audit Compliance',
      priority: 'Medium',
      expectedImpact: '90% action closure rate',
      details: 'Current audit score of 87% is strong, but pending actions have increased 15% month-over-month. Implementing structured follow-up system will prevent backlog accumulation.',
      actionSteps: [
        'Categorize pending actions by risk level and implementation complexity',
        'Assign dedicated owners with clear accountability',
        'Schedule weekly progress reviews for high-priority items',
        'Automate reminder notifications 48 hours before due dates'
      ],
      basedOn: ['Audit Performance Data', 'Action Closure Rates', 'Compliance Trends']
    },
    {
      title: 'Expand Unsafe Acts Reporting Culture',
      summary: 'Unsafe acts reporting has decreased 12%, suggesting potential underreporting. Engagement initiatives recommended.',
      category: 'Behavioral Safety',
      priority: 'Low',
      expectedImpact: '40% increase in reporting',
      details: 'Lower reporting volumes don\'t correlate with improved safety metrics, indicating possible reporting fatigue or fear of consequences. Anonymous reporting and positive reinforcement can reverse this trend.',
      actionSteps: [
        'Launch anonymous reporting mobile app with simplified interface',
        'Implement positive recognition program for safety observations',
        'Conduct "Safety Voice" workshops emphasizing non-punitive culture',
        'Share monthly reports showing impact of observations on safety improvements'
      ],
      basedOn: ['Unsafe Acts Trends', 'Employee Surveys', 'Cultural Assessment']
    }
  ];

  const permitVolumeData = [
    { month: "Jul", active: 185, completed: 142, expired: 8 },
    { month: "Aug", active: 198, completed: 156, expired: 12 },
    { month: "Sep", active: 215, completed: 178, expired: 6 },
    { month: "Oct", active: 232, completed: 195, expired: 9 },
    { month: "Nov", active: 247, completed: 210, expired: 5 },
    { month: "Dec", active: 265, completed: 228, expired: 7 }
  ];

  const hazardDistributionData = [
    { name: "Chemical Exposure", value: 145 },
    { name: "Electrical Hazards", value: 98 },
    { name: "Fall Risks", value: 76 },
    { name: "Fire Hazards", value: 54 },
    { name: "Confined Space", value: 42 }
  ];

  const locationComparisonData = [
    { location: "Plant 1", permits: 98, incidents: 1, compliance: 96 },
    { location: "Plant 2", permits: 76, incidents: 0, compliance: 98 },
    { location: "Plant 3", permits: 54, incidents: 2, compliance: 92 },
    { location: "Warehouse 1", permits: 19, incidents: 0, compliance: 100 }
  ];

  const complianceData = [
    { month: "Jul", compliance: 94, target: 95 },
    { month: "Aug", compliance: 96, target: 95 },
    { month: "Sep", compliance: 93, target: 95 },
    { month: "Oct", compliance: 97, target: 95 },
    { month: "Nov", compliance: 95, target: 95 },
    { month: "Dec", compliance: 98, target: 95 }
  ];

  const hazardAnalyticsData = [
    {
      type: "Chemical Exposure",
      icon: "Droplet",
      occurrences: 145,
      severity: "High",
      incidents: 1,
      trendDirection: "down",
      trendValue: "12%"
    },
    {
      type: "Electrical Hazards",
      icon: "Zap",
      occurrences: 98,
      severity: "Critical",
      incidents: 0,
      trendDirection: "down",
      trendValue: "8%"
    },
    {
      type: "Fall Risks",
      icon: "ArrowDown",
      occurrences: 76,
      severity: "High",
      incidents: 2,
      trendDirection: "up",
      trendValue: "5%"
    },
    {
      type: "Fire Hazards",
      icon: "Flame",
      occurrences: 54,
      severity: "Critical",
      incidents: 0,
      trendDirection: "down",
      trendValue: "15%"
    },
    {
      type: "Confined Space",
      icon: "Box",
      occurrences: 42,
      severity: "Medium",
      incidents: 0,
      trendDirection: "down",
      trendValue: "3%"
    }
  ];

  const safetyEquipmentData = [
    { equipment: "Hard Hats", usage: 247, available: 300 },
    { equipment: "Safety Goggles", usage: 198, available: 250 },
    { equipment: "Respirators", usage: 145, available: 180 },
    { equipment: "Safety Harness", usage: 76, available: 100 },
    { equipment: "Fire Extinguishers", usage: 54, available: 80 }
  ];

  const handleExport = (format) => {
    console.log(`Exporting analytics in ${format} format`);
    alert(`Analytics report will be exported in ${format?.toUpperCase()} format`);
  };

  return (
    <div className={`bg-background`}>
      <div className="p-6 lg:p-8">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Safety Analytics Dashboard</h1>
              <p className="text-muted-foreground mt-1">
                Comprehensive analysis and insights across all safety metrics
              </p>
            </div>
            <Button
              variant="outline"
              iconName="Home"
              onClick={() => navigate('/')}
            >
              Return to Dashboard
            </Button>
          </div>
        </div>

        <div className="mb-6">
          <FilterPanel
            dateRange={dateRange}
            setDateRange={setDateRange}
            selectedLocation={selectedLocation}
            setSelectedLocation={setSelectedLocation}
            permitType={permitType}
            setPermitType={setPermitType}
            onExport={handleExport}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {metricsData?.map((metric, index) => (
            <MetricCard key={index} {...metric} />
          ))}
        </div>

        {/* New Behavioral Safety Indicators Section */}
        <div className="mb-8">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-foreground mb-1">Behavioral Safety Indicators</h2>
            <p className="text-sm text-muted-foreground">
              Comprehensive tracking of safety incidents and observations across all categories
            </p>
          </div>
          <BehavioralSafetyMetrics data={behavioralSafetyData} />
        </div>

        {/* Behavioral Safety Trend Chart */}
        <div className="mb-8">
          <ChartCard 
            title="Behavioral Safety Trends" 
            subtitle="Historical trend analysis for all safety incident categories"
            actions={null}
          >
            <BehavioralSafetyTrendChart data={behavioralSafetyTrendData} />
          </ChartCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <ChartCard 
            title="Permit Volume Trends" 
            subtitle="Monthly permit activity across all locations"
            actions={null}
          >
            <PermitVolumeChart data={permitVolumeData} />
          </ChartCard>

          <ChartCard 
            title="Hazard Category Distribution" 
            subtitle="Breakdown of identified hazards by type"
            actions={null}
          >
            <HazardDistributionChart data={hazardDistributionData} />
          </ChartCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <ChartCard 
            title="Location Safety Comparison" 
            subtitle="Performance benchmarking across facilities"
            actions={null}
          >
            <LocationComparisonChart data={locationComparisonData} />
          </ChartCard>

          <ChartCard 
            title="Compliance Rate Trends" 
            subtitle="Monthly compliance performance vs target"
            actions={null}
          >
            <ComplianceChart data={complianceData} />
          </ChartCard>
        </div>

        <div className="mb-8">
          <ChartCard 
            title="Incident Management Analytics" 
            subtitle="Status tracking and trends across incident categories"
            actions={null}
          >
            <IncidentManagementAnalytics 
              data={incidentManagementData} 
              summaryMetrics={incidentSummaryMetrics}
            />
          </ChartCard>
        </div>

        <div className="mb-8">
          <ChartCard 
            title="Safety Audit Performance" 
            subtitle="Audit score distribution and performance metrics"
            actions={null}
          >
            <SafetyAuditPerformance 
              data={auditPerformanceData}
              scoreData={auditScoreData}
            />
          </ChartCard>
        </div>

        <div className="mb-8">
          <ChartCard 
            title="Key Risks Status" 
            subtitle="Critical safety risks requiring attention and mitigation tracking"
            actions={null}
          >
            <KeyRisksStatus risks={keyRisksData} />
          </ChartCard>
        </div>

        <div className="mb-8">
          <ChartCard 
            title="Hazard Analytics Details" 
            subtitle="Detailed breakdown of hazard occurrences and incident correlation"
            actions={null}
          >
            <HazardAnalyticsTable data={hazardAnalyticsData} />
          </ChartCard>
        </div>

        <div className="mb-8">
          <ChartCard 
            title="Safety Equipment Usage" 
            subtitle="Current usage vs available inventory"
            actions={null}
          >
            <SafetyEquipmentChart data={safetyEquipmentData} />
          </ChartCard>
        </div>

        <div className="mb-8">
          <ChartCard 
            title="AI-Powered Safety Recommendations" 
            subtitle="Data-driven insights and actionable recommendations based on comprehensive analytics"
            actions={null}
          >
            <AIRecommendations recommendations={aiRecommendationsData} />
          </ChartCard>
        </div>

        <div className="bg-card rounded-lg border border-border p-6 shadow-card">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="Info" size={24} color="var(--color-accent)" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Summary Analytics</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Overall safety performance across all locations shows a positive trend with 95% compliance rate. Active permits have increased by 12.5% while safety incidents decreased by 40% compared to the previous period. Chemical exposure remains the most common hazard type, requiring continued monitoring and preventive measures.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-xs text-muted-foreground mb-1">Average Approval Time</p>
                  <p className="text-2xl font-bold text-foreground">4.2 hrs</p>
                  <p className="text-xs text-success mt-1">-15% from last month</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-xs text-muted-foreground mb-1">Permit Extension Rate</p>
                  <p className="text-2xl font-bold text-foreground">8.3%</p>
                  <p className="text-xs text-warning mt-1">+2% from last month</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-xs text-muted-foreground mb-1">Safety Training Hours</p>
                  <p className="text-2xl font-bold text-foreground">1,247</p>
                  <p className="text-xs text-success mt-1">+18% from last month</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SafetyAnalyticsDashboard = () => {
  return (
    <SidebarProvider>
      <div className="flex min-h-screen bg-background">
        <Sidebar userRole="Safety Manager" activeModules={['permits', 'analytics']} />
        <SafetyAnalyticsDashboardContent />
      </div>
    </SidebarProvider>
  );
};

export default SafetyAnalyticsDashboard;